import React, { useState, useMemo, useRef, useEffect } from 'react';
import MegaMenuBusiness from '../mega-menu/MegaMenuBusiness';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import { Wrapper } from '../../utils/components';
import {
  getLinkInformation,
  getMaxNumberOfProducts,
} from '../../utils/functions';
import { Link } from '../link';
import useBlockScroll from '../../utils/hooks/useBlockScroll';
import { PrevArrow, NextArrow } from '../../molecules/arrows/arrows';
import throttle from 'lodash.throttle';
import Chevron from '../../atoms/static-icons/chevron';

const MenuBusinessFirstLevelItem = ({
  item,
  itemKey,
  hasClicked,
  sethasClicked,
  openBurger,
  setSearchOpen,
  maxNumberOfProducts,
}) => {
  const linkInformation = getLinkInformation(
    item.content.firstLevelInternalLink?.value?.link,
    item.content.firstLevelExternalLink.value,
    item.value,
    item.content.firstLevelInternalLink?.value?.pageTitle?.value
  );

  return (
    <li
      key={itemKey}
      className={classNames('main-nav-primary__item', {
        'main-nav-primary__item--active': hasClicked === itemKey,
      })}
    >
      <Wrapper
        className={classNames('main-nav-primary-link', {
          'main-nav-primary-link--active': hasClicked === itemKey,
        })}
        onClick={
          !linkInformation.link
            ? () => {
                setSearchOpen(false);
                sethasClicked((current) =>
                  openBurger ? itemKey : current === itemKey ? -1 : itemKey
                );
              }
            : null
        }
        tag='button'
        component={linkInformation.link && Link}
        to={linkInformation.link}
      >
        {linkInformation.label && <span>{linkInformation.label}</span>}
        {!linkInformation.link && (
          <span
            className={classNames('nav-primary-chevron', {
              'nav-primary-chevron--up': hasClicked === itemKey,
            })}
          >
            <Chevron />
          </span>
        )}
      </Wrapper>
      {!linkInformation.link && (
        <MegaMenuBusiness
          opened={hasClicked === itemKey}
          data={item.content}
          maxNumberOfProducts={maxNumberOfProducts}
        />
      )}
    </li>
  );
};

function NavigationBusiness(
  { langCode, openBurger, setSearchOpen },
  navigationMenuRef
) {
  const { menuHeaderPages } = useStaticQuery(
    graphql`
      {
        menuHeaderPages: allLiferayMenuHeaderMainBusiness(
          filter: {
            liferayFields: { title: { eq: "Menu - Header - Main - Business" } }
          }
        ) {
          nodes {
            liferayFields {
              langCode
            }
            disableArrows {
              value
            }
            firstLevelLabelLink {
              value
              content {
                firstLevelInternalLink {
                  value {
                    ... on LiferayPageGeneric {
                      link
                      pageTitle {
                        value
                      }
                    }
                    ... on LiferayModuleDetailProduct {
                      link
                      pageTitle: detailPageTitle {
                        value
                      }
                    }
                    ... on LiferayModuleDetailBlog {
                      link
                      pageTitle: detailPageTitle {
                        value
                      }
                    }
                    ... on LiferayModuleDetailIniziative {
                      link
                      pageTitle: detailPageTitle {
                        value
                      }
                    }
                  }
                }
                firstLevelExternalLink {
                  value
                }
                firstLevelNotVisible {
                  value
                }
                secondLevelPanel {
                  content {
                    panelNotVisible {
                      value
                    }
                    titlePanel {
                      value
                      content {
                        titleLinkPanel {
                          value
                        }
                      }
                    }
                    descriptionPanel {
                      value
                    }
                    imagePanel {
                      value {
                        title
                      }
                      node {
                        gatsbyImageData(width: 290)
                      }
                    }
                    detailModulePanel {
                      value {
                        __typename
                        ... on LiferayModuleDetailProduct {
                          link
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                          detailPageTitle {
                            value
                          }
                          cardDetails {
                            content {
                              cardImage {
                                value {
                                  title
                                }
                                node {
                                  gatsbyImageData(width: 290)
                                }
                              }
                              cardTitle {
                                value
                              }
                            }
                          }
                        }
                        ... on LiferayModuleDetailBlog {
                          link
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                          detailPageTitle {
                            value
                          }
                          blogPost {
                            content {
                              imageDetailPost {
                                value {
                                  title
                                }
                                node {
                                  gatsbyImageData(width: 290)
                                }
                              }
                            }
                          }
                          cardDetails {
                            content {
                              cardTitle {
                                value
                              }
                            }
                          }
                        }
                        ... on LiferayModuleDetailIniziative {
                          link
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                          detailPageTitle {
                            value
                          }
                          blogPost {
                            content {
                              imageDetailPost {
                                value {
                                  title
                                }
                                node {
                                  gatsbyImageData(width: 290)
                                }
                              }
                            }
                          }
                          cardDetails {
                            content {
                              cardTitle {
                                value
                              }
                            }
                          }
                        }
                      }
                    }
                    labelLinkPanel {
                      value
                      content {
                        internalLinkPanel {
                          value {
                            ... on LiferayPageGeneric {
                              link
                              pageTitle {
                                value
                              }
                            }
                            ... on LiferayModuleDetailProduct {
                              link
                              pageTitle: detailPageTitle {
                                value
                              }
                            }
                            ... on LiferayModuleDetailBlog {
                              link
                              pageTitle: detailPageTitle {
                                value
                              }
                            }
                            ... on LiferayModuleDetailIniziative {
                              link
                              pageTitle: detailPageTitle {
                                value
                              }
                            }
                          }
                        }
                        externalLinkPanel {
                          value
                        }
                      }
                    }
                  }
                }
                secondLevelLabelLink {
                  value
                  content {
                    secondLevelInternalLink {
                      value {
                        ... on LiferayPageGeneric {
                          link
                          pageTitle {
                            value
                          }
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                        }
                        ... on LiferayModuleDetailProduct {
                          link
                          pageTitle: detailPageTitle {
                            value
                          }
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                        }
                        ... on LiferayModuleDetailBlog {
                          link
                          pageTitle: detailPageTitle {
                            value
                          }
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                        }
                        ... on LiferayModuleDetailIniziative {
                          link
                          pageTitle: detailPageTitle {
                            value
                          }
                          liferayFields {
                            taxonomyCategoryBriefs {
                              name
                              description
                            }
                          }
                        }
                      }
                    }
                    secondLevelExternalLink {
                      value
                    }
                    secondLevelNotVisible {
                      value
                    }
                    thirdLevelLabelLink {
                      value
                      content {
                        thirdLevelInternalLink {
                          value {
                            ... on LiferayPageGeneric {
                              link
                              pageTitle {
                                value
                              }
                              liferayFields {
                                taxonomyCategoryBriefs {
                                  name
                                  description
                                }
                              }
                            }
                            ... on LiferayModuleDetailProduct {
                              link
                              pageTitle: detailPageTitle {
                                value
                              }
                            }
                            ... on LiferayModuleDetailBlog {
                              link
                              pageTitle: detailPageTitle {
                                value
                              }
                            }
                            ... on LiferayModuleDetailIniziative {
                              link
                              pageTitle: detailPageTitle {
                                value
                              }
                            }
                          }
                        }
                        thirdLevelExternalLink {
                          value
                        }
                        thirdLevelNotVisible {
                          value
                        }
                        thirdLevelLinkAsButton {
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
  );

  const menu = useMemo(() => {
    return menuHeaderPages.nodes.find(
      (node) => node.liferayFields.langCode === langCode
    );
  }, [menuHeaderPages, langCode]);

  const firstLevelPages = useMemo(() => {
    return menu?.firstLevelLabelLink
      ?.map((item) => {
        return {
          ...item,
          content: {
            ...item.content,
            secondLevelPages: item.content?.secondLevelLabelLink
              ?.map((subItem) => {
                return {
                  ...subItem,
                  content: {
                    ...subItem.content,
                    thirdLevelPages:
                      subItem.content?.thirdLevelLabelLink?.filter(
                        (subSubItem) =>
                          (subSubItem?.value ||
                            subSubItem.content?.thirdLevelExternalLink?.value ||
                            subSubItem.content?.thirdLevelInternalLink?.value
                              ?.link) &&
                          !subSubItem.content?.thirdLevelNotVisible?.value
                      ),
                  },
                };
              })
              ?.filter(
                (subItem) =>
                  (subItem?.value ||
                    subItem.content?.secondLevelExternalLink?.value ||
                    subItem.content?.secondLevelInternalLink?.value?.link) &&
                  !subItem.content?.secondLevelNotVisible?.value
              ),
          },
        };
      })
      ?.filter(
        (item) =>
          (item?.value ||
            item.content?.firstLevelExternalLink?.value ||
            item.content?.firstLevelInternalLink?.value?.link) &&
          !item.content?.firstLevelNotVisible?.value
      );
  }, [menu]);

  const [hasClicked, sethasClicked] = useState(-1);
  const [showArrowLeft, setshowArrowLeft] = useState(false);
  const [showArrowRight, setshowArrowRight] = useState(false);

  const menuRef = useRef();

  useEffect(() => {
    if (hasClicked >= 0) {
      const listener = (event) => {
        if (
          event.target !== menuRef.current &&
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          !openBurger
        ) {
          sethasClicked(-1);
        }
      };

      document.addEventListener('click', listener);

      return () => document.removeEventListener('click', listener);
    }
  }, [hasClicked, openBurger]);

  useEffect(() => {
    if (openBurger) {
      sethasClicked((current) => (current === -1 ? 0 : current));
    } else {
      sethasClicked(-1);
    }
  }, [openBurger]);

  useBlockScroll(openBurger); // Call hook to hide scroll page

  // Arrows management
  const scrollContainer = (direction) => {
    let amount = direction * (menuRef.current.clientWidth / 2);
    menuRef.current.scrollLeft += amount;
  };

  useEffect(() => {
    if (openBurger) {
      const windowListener = throttle(() => {
        let children = menuRef.current.children;
        let totalWidth = 90; // This to keep in the total the padding of <nav>

        for (let i = 0; i < children.length; i++) {
          let childrenStyle = window.getComputedStyle(children[i]);
          let childrenMarginRight = parseInt(
            childrenStyle.getPropertyValue('margin-right')
          );
          let childrenMarginLeft = parseInt(
            childrenStyle.getPropertyValue('margin-left')
          );
          totalWidth +=
            children[i].clientWidth + childrenMarginLeft + childrenMarginRight;
        }

        if (totalWidth > menuRef.current.clientWidth) {
          setshowArrowRight(true);
        } else {
          setshowArrowRight(false);
        }
      }, 250);

      const swipeListener = throttle(() => {
        // Hide right arrow because the scrollbar is at the end
        if (
          menuRef.current.offsetWidth + menuRef.current.scrollLeft ===
          menuRef.current.scrollWidth
        ) {
          setshowArrowRight(false);
        }

        // Show right arrow because the scrollbar is moving in left direction
        if (
          menuRef.current.offsetWidth + menuRef.current.scrollLeft <
          menuRef.current.scrollWidth
        ) {
          setshowArrowRight(true);
        }

        // Hide left arrow because the scrollbar is at the begin
        if (menuRef.current.scrollLeft === 0) {
          setshowArrowLeft(false);
        }

        // Show left arrow because the scrollbar is moving in right direction
        if (menuRef.current.scrollLeft > 0) {
          setshowArrowLeft(true);
        }
      }, 250);

      const currentSlider = menuRef.current;
      currentSlider.addEventListener('scroll', swipeListener);
      window.addEventListener('resize', windowListener);
      windowListener();

      return () => {
        currentSlider.removeEventListener('scroll', swipeListener);
        window.removeEventListener('resize', windowListener);
      };
    }
  }, [openBurger]);

  return (
    <nav
      ref={navigationMenuRef}
      className={classNames('main-header__navigation', {
        'main-header__navigation--mobile': openBurger,
      })}
    >
      {menu?.disableArrows?.value === false && openBurger && showArrowLeft && (
        <PrevArrow
          className={classNames('arrow', {
            'show-arrow-left': showArrowLeft,
          })}
          onClick={() => scrollContainer(-1)}
        />
      )}
      <ul className='main-nav-primary' ref={menuRef}>
        {firstLevelPages?.map((firstLevelPage, key) => (
          <MenuBusinessFirstLevelItem
            key={key}
            item={firstLevelPage}
            itemKey={key}
            hasClicked={hasClicked}
            sethasClicked={sethasClicked}
            openBurger={openBurger}
            setSearchOpen={setSearchOpen}
            maxNumberOfProducts={getMaxNumberOfProducts(firstLevelPages)}
          />
        ))}
      </ul>
      {menu?.disableArrows?.value === false && openBurger && showArrowRight && (
        <NextArrow
          className={classNames('arrow', {
            'show-arrow-right': showArrowRight,
          })}
          onClick={() => scrollContainer(1)}
        />
      )}
    </nav>
  );
}

export default React.forwardRef(NavigationBusiness);
